import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import measurementService from '../../services/measurementService';
import {
  formatMeasurementTableTime,
  getMeasurementWallClockDateYmd,
  getMeasurementWallClockTimeHms
} from '../../utils/measurementUtils';
import './Trend7dCell.css';

/**
 * Standard default vital target ranges (used if patient target settings are not customized)
 */
const DEFAULT_VITAL_TARGETS = {
  'Blood Pressure': { sysMin: 90, sysMax: 140, diaMin: 60, diaMax: 90 },
  'Blood Glucose': { min: 70, max: 140 },
  'Weight': { minLbs: 66, maxLbs: 220 },
  'Pulse Oximeter': { minSpo2: 95 }
};

function hasPositiveVital(value) {
  if (value == null || value === '' || value === 'N/A') return false;
  const num = Number(value);
  return Number.isFinite(num) && num > 0;
}

function sortableVitalTs(value) {
  if (value == null || value === '') return '';
  return String(value)
    .trim()
    .replace('T', ' ')
    .replace(/\.\d+Z?$/, '')
    .replace(/Z$/, '');
}

function getVitalLatestDate(patient, vitalName) {
  if (!patient) return '';
  if (vitalName === 'Blood Pressure') {
    return sortableVitalTs(
      patient.last_abnormal_bp_at ||
      patient.last_bp_at ||
      patient.last_bp_date ||
      ''
    );
  }
  if (vitalName === 'Blood Glucose') {
    return sortableVitalTs(
      patient.last_abnormal_bg_at ||
      patient.last_bg_at ||
      patient.last_bg_date ||
      ''
    );
  }
  if (vitalName === 'Weight') {
    return sortableVitalTs(
      patient.last_abnormal_wt_at ||
      patient.last_wt_at ||
      patient.last_wt_date ||
      patient.last_weight_date ||
      ''
    );
  }
  return '';
}

function hasVitalReading(patient, vitalName) {
  if (!patient) return false;
  if (vitalName === 'Blood Pressure') {
    return (
      hasPositiveVital(patient.last_systolic) ||
      hasPositiveVital(patient.systolic) ||
      hasPositiveVital(patient.last_diastolic) ||
      hasPositiveVital(patient.diastolic)
    );
  }
  if (vitalName === 'Blood Glucose') {
    return hasPositiveVital(patient.last_glucose) || hasPositiveVital(patient.glucose);
  }
  if (vitalName === 'Weight') {
    return (
      hasPositiveVital(patient.last_weight) ||
      hasPositiveVital(patient.weight) ||
      Boolean(patient.last_wt_at || patient.last_abnormal_wt_at || patient.last_wt_date)
    );
  }
  return false;
}

/**
 * Parse enrolled vitals, keep only types that have readings, and put the
 * vital with the most recent reading (date AND time) first.
 */
function getPatientEnrolledVitals(patient) {
  if (!patient) return ['Blood Pressure'];
  const raw = patient.vitals || patient.enrolled_vitals || patient.vital_services || '';
  let list = String(raw)
    .split(',')
    .map((v) => normalizeVitalName(v.trim()))
    .filter((v) => v === 'Blood Pressure' || v === 'Blood Glucose' || v === 'Weight')
    .filter((v, idx, arr) => arr.indexOf(v) === idx);

  if (list.length === 0) {
    const detected = [];
    if (hasVitalReading(patient, 'Blood Pressure')) detected.push('Blood Pressure');
    if (hasVitalReading(patient, 'Blood Glucose')) detected.push('Blood Glucose');
    if (hasVitalReading(patient, 'Weight')) detected.push('Weight');
    list = detected.length > 0 ? detected : ['Blood Pressure'];
  }

  const withReadings = list.filter((vital) => hasVitalReading(patient, vital));
  const pool = withReadings.length > 0 ? withReadings : list;

  return [...pool].sort((a, b) => {
    const dateA = getVitalLatestDate(patient, a);
    const dateB = getVitalLatestDate(patient, b);
    if (dateA !== dateB) return dateB.localeCompare(dateA);
    const hasA = hasVitalReading(patient, a) ? 1 : 0;
    const hasB = hasVitalReading(patient, b) ? 1 : 0;
    return hasB - hasA;
  });
}

/**
 * Normalizes vital name for display / matching
 */
function normalizeVitalName(name) {
  const n = String(name || '').toLowerCase().trim();
  if (n.includes('bp') || n.includes('pressure')) return 'Blood Pressure';
  if (n.includes('bg') || n.includes('glucose')) return 'Blood Glucose';
  if (n.includes('wt') || n.includes('weight') || n === 'w') return 'Weight';
  if (n.includes('pulse') || n.includes('spo2') || n.includes('oximeter')) return 'Pulse Oximeter';
  return name || 'Blood Pressure';
}

/**
 * Returns shorthand name for table display: BP, BG, W, SpO2
 */
function getVitalShorthand(vitalName) {
  const norm = normalizeVitalName(vitalName);
  if (norm === 'Blood Pressure') return 'BP';
  if (norm === 'Blood Glucose') return 'BG';
  if (norm === 'Weight') return 'W';
  if (norm === 'Pulse Oximeter') return 'SpO2';
  return vitalName || 'BP';
}

/**
 * Generate dates for the last 7 calendar days ending today
 */
function getLast7Days() {
  const days = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    days.push({
      dateStr: `${y}-${m}-${day}`,
      label,
      isToday: i === 0
    });
  }
  return days;
}

/**
 * Evaluates whether a daily reading / average reading is abnormal based on target settings
 */
function isReadingAbnormal(vitalType, avgData, patient) {
  if (!avgData) return false;
  const targets = DEFAULT_VITAL_TARGETS[vitalType] || {};

  const parseNum = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const n = Number(val);
    return Number.isNaN(n) ? null : n;
  };

  if (vitalType === 'Blood Pressure') {
    const sys = parseNum(avgData.sysAvg ?? avgData.sys);
    const dia = parseNum(avgData.diaAvg ?? avgData.dia);
    if (sys != null && sys > 0) {
      const sysMax = parseNum(patient?.target_sys_max) ?? targets.sysMax ?? 140;
      const sysMin = parseNum(patient?.target_sys_min) ?? targets.sysMin ?? 100;
      if (sys > sysMax || sys < sysMin) return true;
    }
    if (dia != null && dia > 0) {
      const diaMax = parseNum(patient?.target_dia_max) ?? targets.diaMax ?? 90;
      const diaMin = parseNum(patient?.target_dia_min) ?? targets.diaMin ?? 60;
      if (dia > diaMax || dia < diaMin) return true;
    }
    return false;
  }

  if (vitalType === 'Blood Glucose') {
    const val = parseNum(avgData.valAvg ?? avgData.value ?? avgData.glucose);
    if (val != null && val > 0) {
      const max = parseNum(patient?.target_bg_max) ?? targets.max ?? 110;
      const min = parseNum(patient?.target_bg_min) ?? targets.min ?? 60;
      if (val > max || val < min) return true;
    }
    return false;
  }

  if (vitalType === 'Weight') {
    const val = parseNum(avgData.valAvg ?? avgData.value ?? avgData.weight);
    if (val != null && val > 0) {
      const max = parseNum(patient?.target_weight_max) ?? targets.maxLbs ?? 220;
      const min = parseNum(patient?.target_weight_min) ?? targets.minLbs ?? 66;
      if (val > max || val < min) return true;
    }
    return false;
  }

  if (vitalType === 'Pulse Oximeter') {
    const val = parseNum(avgData.valAvg ?? avgData.value ?? avgData.spo2);
    if (val != null && val > 0) {
      const min = parseNum(patient?.target_spo2_min) ?? targets.minSpo2 ?? 95;
      if (val < min) return true;
    }
    return false;
  }

  return Boolean(avgData.isAbnormal);
}

/**
 * Calculates dynamic height in px (between 8px and 26px) for trend bar based on daily average reading
 */
function calculateBarHeight(vitalType, avgData) {
  if (!avgData) return 8;

  if (vitalType === 'Blood Pressure') {
    const sys = Number(avgData.sysAvg ?? avgData.sys ?? 120);
    // Range 80 to 180 mmHg
    const ratio = Math.min(1, Math.max(0, (sys - 80) / (180 - 80)));
    return Math.round(8 + ratio * 18);
  }

  if (vitalType === 'Blood Glucose') {
    const bg = Number(avgData.valAvg ?? avgData.value ?? avgData.glucose ?? 100);
    // Range 60 to 240 mg/dL
    const ratio = Math.min(1, Math.max(0, (bg - 60) / (240 - 60)));
    return Math.round(8 + ratio * 18);
  }

  if (vitalType === 'Weight') {
    const wt = Number(avgData.valAvg ?? avgData.value ?? avgData.weight ?? 160);
    // Range 100 to 260 lbs
    const ratio = Math.min(1, Math.max(0, (wt - 100) / (260 - 100)));
    return Math.round(8 + ratio * 18);
  }

  if (vitalType === 'Pulse Oximeter') {
    const spo2 = Number(avgData.valAvg ?? avgData.value ?? avgData.spo2 ?? 98);
    // Range 85 to 100%
    const ratio = Math.min(1, Math.max(0, (spo2 - 85) / (100 - 85)));
    return Math.round(8 + ratio * 18);
  }

  return 18;
}

function getBpSysStatus(sys, patient) {
  if (sys == null || Number.isNaN(Number(sys)) || Number(sys) <= 0) return 'normal';
  const val = Number(sys);
  const sysMax = Number(patient?.target_sys_max) || 140;
  const sysMin = Number(patient?.target_sys_min) || 100;
  if (val > sysMax) return 'high';
  if (val < sysMin) return 'low';
  return 'normal';
}

function getBpDiaStatus(dia, patient) {
  if (dia == null || Number.isNaN(Number(dia)) || Number(dia) <= 0) return 'normal';
  const val = Number(dia);
  const diaMax = Number(patient?.target_dia_max) || 90;
  const diaMin = Number(patient?.target_dia_min) || 60;
  if (val > diaMax) return 'high';
  if (val < diaMin) return 'low';
  return 'normal';
}

function getSingleVitalStatus(vitalType, val, patient) {
  if (val == null || Number.isNaN(Number(val)) || Number(val) <= 0) return 'normal';
  const v = Number(val);
  if (vitalType === 'Blood Glucose') {
    const max = Number(patient?.target_bg_max) || 140;
    const min = Number(patient?.target_bg_min) || 70;
    if (v > max) return 'high';
    if (v < min) return 'low';
    return 'normal';
  }
  if (vitalType === 'Weight') {
    const max = Number(patient?.target_weight_max) || 220;
    const min = Number(patient?.target_weight_min) || 66;
    if (v > max) return 'high';
    if (v < min) return 'low';
    return 'normal';
  }
  return 'normal';
}

function getStatusColor(status) {
  if (status === 'high') return '#dc2626'; // Red
  if (status === 'low') return '#d97706';  // Orange
  return '#166534'; // Green
}

function parsePositiveNumber(raw) {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function readTrendVitalValues(row, vitalType) {
  if (!row) return [];
  if (vitalType === 'Blood Glucose') {
    const seen = new Set();
    const values = [];
    [
      row.blood_glucose_value_1,
      row.blood_glucose_value,
      row.glucose_value,
      row.glucose,
      row.blood_glucose_value_2,
      row.blood_glucose_value_3
    ].forEach((raw) => {
      const n = parsePositiveNumber(raw);
      if (!n || seen.has(n)) return;
      seen.add(n);
      values.push(n);
    });
    return values;
  }
  if (vitalType === 'Weight') {
    const n = parsePositiveNumber(row.weight_lbs ?? row.weight ?? row.value);
    return n ? [n] : [];
  }
  if (vitalType === 'Pulse Oximeter') {
    const n = parsePositiveNumber(row.spo2 ?? row.oxygen_saturation ?? row.value);
    return n ? [n] : [];
  }
  const n = parsePositiveNumber(row.glucose ?? row.weight ?? row.spo2 ?? row.value);
  return n ? [n] : [];
}

function isDailyAggregateTrendRow(row) {
  if (!row || typeof row !== 'object') return false;
  const hasAggregate = row.sysAvg != null || row.diaAvg != null || row.valAvg != null || Boolean(row.displayValue);
  const hasRawBp = parsePositiveNumber(row.systolic_pressure ?? row.systolic) > 0;
  const hasRawReading = readTrendVitalValues(row, 'Blood Glucose').length > 0
    || readTrendVitalValues(row, 'Weight').length > 0
    || hasRawBp;
  return hasAggregate && !hasRawReading;
}

function extractMeasurementRows(res) {
  const payload = res?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.measurements)) return payload.measurements;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.measurements)) return payload.data.measurements;
  return [];
}

function vitalUnit(vitalType) {
  if (vitalType === 'Blood Glucose') return 'mg/dL';
  if (vitalType === 'Weight') return 'lbs';
  if (vitalType === 'Pulse Oximeter') return '%';
  return '';
}

function formatSingleVitalDisplay(vitalType, val) {
  const unit = vitalUnit(vitalType);
  const num = vitalType === 'Weight' ? Number(val).toFixed(1) : Math.round(Number(val));
  return unit ? `${num} ${unit}` : String(num);
}

function readTrendDateKey(row) {
  return (
    getMeasurementWallClockDateYmd(row) ||
    String(row?.dateStr || row?.date_recorded || row?.date || '').split('T')[0] ||
    ''
  );
}

function readTrendTimeLabel(row) {
  const formatted = formatMeasurementTableTime(getMeasurementWallClockTimeHms(row) || row?.measurement_time || row?.time_recorded || row?.time || '');
  if (formatted) return formatted;
  const timeStr = row?.measurement_time || row?.time_recorded || row?.time || '';
  if (!timeStr) return '';
  try {
    const [hStr, mStr] = String(timeStr).split(':');
    let h = parseInt(hStr, 10);
    if (!Number.isFinite(h) || !mStr) return String(timeStr);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${mStr.padStart(2, '0')} ${ampm}`;
  } catch {
    return String(timeStr);
  }
}

function readReviewMeta(row) {
  const name = String(row?.reviewed_by_name || '').trim();
  const role = String(row?.reviewed_by_role || '').trim();
  const at = row?.reviewed_at || null;
  return {
    reviewedByName: name,
    reviewedByRole: role,
    reviewedAt: at
  };
}

function formatReviewerLine(reading) {
  if (!reading) return '';
  const name = String(reading.reviewedByName || '').trim();
  const role = String(reading.reviewedByRole || '').trim();
  if (!name && !reading.reviewedAt) return '';
  if (!name) return 'Reviewed';
  return role ? `Reviewed by ${name} (${role})` : `Reviewed by ${name}`;
}

const HOVER_CLOSE_DELAY_MS = 2000;

const Trend7dCell = ({ patient, practiceId: propPracticeId }) => {
  const enrolledVitals = useMemo(() => getPatientEnrolledVitals(patient), [patient]);
  const [activeVitalIndex, setActiveVitalIndex] = useState(0);
  const [fetchedMeasurements, setFetchedMeasurements] = useState(null);
  const [fetchedVital, setFetchedVital] = useState(null);

  const firstVital = enrolledVitals[0] || 'Blood Pressure';
  const patientId = patient?.patient_table_id || patient?.id || patient?.patient_id || patient?.user_id;

  useEffect(() => {
    setActiveVitalIndex(0);
  }, [firstVital, patientId]);

  const currentVitalRaw = enrolledVitals[activeVitalIndex % enrolledVitals.length] || 'Blood Pressure';
  const currentVital = normalizeVitalName(currentVitalRaw);
  const vitalShorthand = getVitalShorthand(currentVital);

  const practiceId = patient?.practice_id || patient?.practiceId || propPracticeId || 77;

  useEffect(() => {
    if (!practiceId || !patientId) return;

    let isMounted = true;
    setFetchedMeasurements(null);
    setFetchedVital(currentVital);

    const fetch7dReadings = async () => {
      try {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 90);
        const formatYmd = (d) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        const fromDate = formatYmd(start);
        const toDate = formatYmd(end);

        let res;
        if (currentVital === 'Blood Glucose') {
          res = await measurementService.getBloodGlucoseByDateRange(practiceId, patientId, fromDate, toDate);
        } else if (currentVital === 'Weight') {
          res = await measurementService.getWeightByDateRange(practiceId, patientId, fromDate, toDate);
        } else if (currentVital === 'Blood Pressure') {
          res = await measurementService.getBloodPressureByDateRange(practiceId, patientId, fromDate, toDate);
        } else {
          if (isMounted) setFetchedMeasurements([]);
          return;
        }

        if (isMounted) {
          setFetchedMeasurements(extractMeasurementRows(res));
        }
      } catch (err) {
        if (isMounted) setFetchedMeasurements([]);
      }
    };

    fetch7dReadings();
    return () => { isMounted = false; };
  }, [practiceId, patientId, currentVital, patient?.last_reviewed_at, patient?.latest_abnormal_uploaded_at, patient?.abnormal_episode_pending]);

  const days7 = useMemo(() => getLast7Days(), []);

  // Compute 7-day trend readings & dynamic bar heights for current active vital
  const trendData = useMemo(() => {
    const bundledTrend =
      patient?.vitals_trend_7d?.[currentVital] ||
      patient?.trend_7d?.[currentVital] ||
      patient?.measurements_7d?.[currentVital] ||
      null;
    const localRows =
      (Array.isArray(patient?.measurements) ? patient.measurements : null) ||
      (Array.isArray(patient?.recent_readings) ? patient.recent_readings : null) ||
      (Array.isArray(patient?.readings) ? patient.readings : null) ||
      null;
    const fetchedRows =
      fetchedVital === currentVital && Array.isArray(fetchedMeasurements)
        ? fetchedMeasurements
        : null;

    const looksRaw = (rows) =>
      Array.isArray(rows) && rows.length > 0 && rows.some((row) => !isDailyAggregateTrendRow(row));

    const rawRows = looksRaw(fetchedRows)
      ? fetchedRows
      : looksRaw(localRows)
        ? localRows
        : looksRaw(bundledTrend)
          ? bundledTrend
          : null;
    const aggregateRows = Array.isArray(bundledTrend) ? bundledTrend.filter(isDailyAggregateTrendRow) : [];
    const rawTrendData = [
      ...aggregateRows,
      ...(Array.isArray(rawRows) ? rawRows : [])
    ];

    // Build map from date rows if present
    const dateMap = {};
    if (rawTrendData.length > 0) {
      rawTrendData.forEach((row) => {
        const dateKey = readTrendDateKey(row);
        if (!dateKey) return;
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            count: 0,
            sysSum: 0,
            diaSum: 0,
            valSum: 0,
            valCount: 0,
            sysAvg: null,
            diaAvg: null,
            valAvg: null,
            readingsList: []
          };
        }

        const hasReadyReadings =
          Array.isArray(row.readingsList) &&
          row.readingsList.length > 0 &&
          (row.sysAvg != null || row.valAvg != null || row.displayValue);

        if (hasReadyReadings) {
          dateMap[dateKey] = {
            ...dateMap[dateKey],
            ...row,
            readingsList: [...row.readingsList]
          };
          return;
        }

        if (isDailyAggregateTrendRow(row)) {
          const aggCount = Number(row.count) || 0;
          dateMap[dateKey].count = Math.max(dateMap[dateKey].count, aggCount);
          if (row.sysAvg != null) dateMap[dateKey].sysAvg = Number(row.sysAvg);
          if (row.diaAvg != null) dateMap[dateKey].diaAvg = Number(row.diaAvg);
          if (row.valAvg != null) dateMap[dateKey].valAvg = Number(row.valAvg);
          if (row.displayValue) dateMap[dateKey].displayValue = row.displayValue;
          return;
        }

        const sys = parsePositiveNumber(row.systolic_pressure ?? row.systolic);
        const dia = parsePositiveNumber(row.diastolic_pressure ?? row.diastolic);
        const formattedTime = readTrendTimeLabel(row);

        if (currentVital === 'Blood Pressure') {
          if (sys > 0) dateMap[dateKey].sysSum += sys;
          if (dia > 0) dateMap[dateKey].diaSum += dia;
          if (sys > 0 && dia > 0) {
            dateMap[dateKey].count += 1;
            const sysStatus = getBpSysStatus(sys, patient);
            const diaStatus = getBpDiaStatus(dia, patient);
            dateMap[dateKey].readingsList.push({
              time: formattedTime,
              sys: Math.round(sys),
              dia: Math.round(dia),
              sysStatus,
              diaStatus,
              isAbnormal: sysStatus !== 'normal' || diaStatus !== 'normal',
              ...readReviewMeta(row)
            });
          }
          return;
        }

        const values = readTrendVitalValues(row, currentVital);
        values.forEach((val) => {
          dateMap[dateKey].count += 1;
          dateMap[dateKey].valSum += val;
          dateMap[dateKey].valCount += 1;
          const status = getSingleVitalStatus(currentVital, val, patient);
          dateMap[dateKey].readingsList.push({
            time: formattedTime,
            valDisplay: formatSingleVitalDisplay(currentVital, val),
            status,
            isAbnormal: status !== 'normal',
            ...readReviewMeta(row)
          });
        });
      });

      // Compute averages for aggregated rows
      Object.keys(dateMap).forEach((dateKey) => {
        const item = dateMap[dateKey];
        if (item.readingsList.length > 1) {
          item.readingsList.sort((a, b) => String(b.time || '').localeCompare(String(a.time || '')));
        }
        if (item.count < item.readingsList.length) {
          item.count = item.readingsList.length;
        }
        if (item.readingsList.length > 0) {
          item.count = item.readingsList.length;
          if (currentVital === 'Blood Pressure' && (item.sysSum > 0 || item.diaSum > 0)) {
            const sysDiv = item.readingsList.length;
            item.sysAvg = Math.round(item.sysSum / sysDiv);
            item.diaAvg = Math.round(item.diaSum / sysDiv);
            item.displayValue = `${item.sysAvg}/${item.diaAvg} mmHg`;
          } else if (item.valCount > 0) {
            item.valAvg = item.valSum / item.valCount;
            item.displayValue = formatSingleVitalDisplay(currentVital, item.valAvg);
            item.valAvg = Math.round(item.valAvg);
          }
        } else if (!item.displayValue && item.count > 0) {
          if (currentVital === 'Blood Pressure' && (item.sysSum > 0 || item.diaSum > 0)) {
            item.sysAvg = Math.round(item.sysSum / item.count);
            item.diaAvg = Math.round(item.diaSum / item.count);
            item.displayValue = `${item.sysAvg}/${item.diaAvg} mmHg`;
          } else if (item.valSum > 0) {
            const valDiv = item.valCount || item.count;
            item.valAvg = item.valSum / valDiv;
            item.displayValue = formatSingleVitalDisplay(currentVital, item.valAvg);
            item.valAvg = Math.round(item.valAvg);
          }
        }
      });
    }

    let totalReadingsCount = 0;

    // Use last 7 calendar days ending today (or recent dates with readings if days7 has no readings)
    let slotsToMap = days7;
    const dateMapKeys = Object.keys(dateMap).sort().reverse();
    const hasReadingsInDays7 = days7.some((d) => {
      const item = dateMap[d.dateStr];
      return item && (item.count > 0 || (item.readingsList && item.readingsList.length > 0));
    });
    if (!hasReadingsInDays7 && dateMapKeys.length > 0) {
      const recent7Keys = dateMapKeys.slice(0, 7).reverse();
      slotsToMap = recent7Keys.map((dk) => {
        const parts = dk.split('-');
        let label = dk;
        if (parts.length === 3) {
          const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
          label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return { dateStr: dk, label, isToday: false };
      });
    }

    const resultSlots = slotsToMap.map((dayObj) => {
      const existing = dateMap[dayObj.dateStr];
      const slotCount = Math.max(
        Number(existing?.count) || 0,
        Array.isArray(existing?.readingsList) ? existing.readingsList.length : 0
      );
      if (existing && slotCount > 0) {
        totalReadingsCount += slotCount;
        const abnormal = isReadingAbnormal(currentVital, existing, patient);
        const barHeightPx = calculateBarHeight(currentVital, existing);

        return {
          ...dayObj,
          count: slotCount,
          avgDisplay: existing.displayValue || existing.avgValue || 'Reading',
          sysAvg: existing.sysAvg,
          diaAvg: existing.diaAvg,
          isAbnormal: abnormal,
          barHeightPx,
          readingsList: existing.readingsList || []
        };
      }

      return {
        ...dayObj,
        count: 0,
        avgDisplay: 'No readings',
        sysAvg: null,
        diaAvg: null,
        isAbnormal: false,
        barHeightPx: 6,
        readingsList: []
      };
    });

    return {
      slots: resultSlots,
      totalReadings: totalReadingsCount
    };
  }, [patient, currentVital, days7, fetchedMeasurements, fetchedVital]);

  const [hoveredSlot, setHoveredSlot] = useState(null);
  const hideTimerRef = useRef(null);

  const cancelHidePopover = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const scheduleHidePopover = () => {
    cancelHidePopover();
    hideTimerRef.current = window.setTimeout(() => {
      setHoveredSlot(null);
      hideTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelHidePopover(), []);

  const openSlotPopover = (targetEl, slot, title) => {
    if (!slot || Number(slot.count) <= 0) return;
    cancelHidePopover();
    const rect = targetEl.getBoundingClientRect();
    const placeBelow = rect.top < 220;
    setHoveredSlot({
      slot,
      title: title || slot.label,
      x: Math.min(window.innerWidth - 16, Math.max(16, rect.left + rect.width / 2)),
      y: placeBelow ? rect.bottom + 8 : rect.top - 8,
      placeBelow,
      hideAvg: Boolean(slot.hideAvg)
    });
  };

  const handlePrevVital = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveVitalIndex((prev) => (prev - 1 + enrolledVitals.length) % enrolledVitals.length);
  };

  const handleNextVital = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveVitalIndex((prev) => (prev + 1) % enrolledVitals.length);
  };

  const hasMultipleVitals = enrolledVitals.length > 1;

  return (
    <div
      className="trend-7d-cell"
      onMouseEnter={cancelHidePopover}
      onMouseLeave={scheduleHidePopover}
    >
      {/* Main row: Left Arrow + 7-Day Bar Chart + Right Arrow */}
      <div className="trend-7d-graph-row">
        {hasMultipleVitals ? (
          <button
            type="button"
            className="trend-7d-arrow-btn trend-7d-arrow-left"
            onClick={handlePrevVital}
            title="Previous vital"
            aria-label="Previous vital"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        ) : (
          <div className="trend-7d-arrow-placeholder" />
        )}

        {/* 7-Day Mini Bar Graph with Dynamic Bar Heights */}
        <div className="trend-7d-bars-container">
          {trendData.slots.map((slot, index) => {
            return (
              <div
                key={index}
                className="trend-7d-slot-wrapper"
                onMouseEnter={(e) => openSlotPopover(e.currentTarget, slot)}
              >
                {slot.count > 0 ? (
                  <div
                    className={`trend-7d-bar-fill ${
                      slot.isAbnormal ? 'trend-bar--abnormal' : 'trend-bar--normal'
                    }`}
                    style={{ height: `${slot.barHeightPx}px` }}
                  />
                ) : (
                  <div className="trend-7d-bar-empty" />
                )}
              </div>
            );
          })}
        </div>

        {hasMultipleVitals ? (
          <button
            type="button"
            className="trend-7d-arrow-btn trend-7d-arrow-right"
            onClick={handleNextVital}
            title="Next vital"
            aria-label="Next vital"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        ) : (
          <div className="trend-7d-arrow-placeholder" />
        )}
      </div>

      {/* Footer below graph: Vital Shorthand label (BP, BG, W) & Readings Count */}
      <div
        className="trend-7d-footer"
        onMouseEnter={(e) => {
          const allReadings = (trendData.slots || []).flatMap((day) =>
            (day.readingsList || []).map((reading) => ({ ...reading, dayLabel: day.label }))
          );
          if (allReadings.length === 0) return;
          openSlotPopover(e.currentTarget, {
            label: `${vitalShorthand} this week`,
            count: allReadings.length,
            readingsList: allReadings,
            hideAvg: true
          }, `${vitalShorthand} · ${allReadings.length} reading${allReadings.length === 1 ? '' : 's'} this week`);
        }}
      >
        <span className="trend-7d-vital-badge" title={currentVital}>
          {vitalShorthand}
        </span>
        <span className="trend-7d-subtitle">
          {trendData.totalReadings} reading{trendData.totalReadings === 1 ? '' : 's'} this week
        </span>
      </div>

      {hoveredSlot && hoveredSlot.slot
        ? createPortal(
          <div
            className={`trend-7d-hover-popover${hoveredSlot.placeBelow ? ' is-below' : ''}`}
            style={{
              left: `${hoveredSlot.x}px`,
              top: `${hoveredSlot.y}px`
            }}
            onMouseEnter={cancelHidePopover}
            onMouseLeave={scheduleHidePopover}
          >
            <div className="trend-7d-hover-title">
              {hoveredSlot.title || hoveredSlot.slot.label}
            </div>

            {!hoveredSlot.hideAvg && hoveredSlot.slot.sysAvg != null && hoveredSlot.slot.diaAvg != null ? (
              <div className="trend-7d-hover-avg">
                <span>Avg ({hoveredSlot.slot.count}):</span>
                <span style={{ fontWeight: 700 }}>
                  <span style={{ color: getStatusColor(getBpSysStatus(hoveredSlot.slot.sysAvg, patient)) }}>{hoveredSlot.slot.sysAvg}</span>
                  <span style={{ color: '#64748b', margin: '0 1px' }}>/</span>
                  <span style={{ color: getStatusColor(getBpDiaStatus(hoveredSlot.slot.diaAvg, patient)) }}>{hoveredSlot.slot.diaAvg}</span>
                  <span style={{ color: '#64748b', marginLeft: '3px', fontWeight: 500, fontSize: '9.5px' }}>mmHg</span>
                </span>
              </div>
            ) : !hoveredSlot.hideAvg && hoveredSlot.slot.avgDisplay && hoveredSlot.slot.avgDisplay !== 'No readings' ? (
              <div className="trend-7d-hover-avg">
                <span>Avg ({hoveredSlot.slot.count}):</span>
                <span style={{ fontWeight: 700 }}>
                  <span style={{ color: getStatusColor(getBpSysStatus(hoveredSlot.slot.sysAvg, patient)) }}>{hoveredSlot.slot.sysAvg}</span>
                  <span style={{ color: '#64748b', margin: '0 1px' }}>/</span>
                  <span style={{ color: getStatusColor(getBpDiaStatus(hoveredSlot.slot.diaAvg, patient)) }}>{hoveredSlot.slot.diaAvg}</span>
                  <span style={{ color: '#64748b', marginLeft: '3px', fontWeight: 500, fontSize: '9.5px' }}>mmHg</span>
                </span>
              </div>
            ) : hoveredSlot.slot.avgDisplay && hoveredSlot.slot.avgDisplay !== 'No readings' ? (
              <div className="trend-7d-hover-avg">
                <span>Avg ({hoveredSlot.slot.count}):</span>
                <span style={{ color: '#0284c7', fontWeight: 700 }}>{hoveredSlot.slot.avgDisplay}</span>
              </div>
            ) : null}

            {hoveredSlot.slot.readingsList && hoveredSlot.slot.readingsList.length > 0 && (
              <div>
                <div className="trend-7d-hover-list-label">
                  All Readings ({hoveredSlot.slot.readingsList.length}):
                </div>
                <div className="trend-7d-hover-list">
                  {hoveredSlot.slot.readingsList.map((r, idx) => {
                    const reviewerLine = formatReviewerLine(r);
                    return (
                      <div key={idx} className={`trend-7d-hover-row${idx % 2 === 0 ? ' is-alt' : ''}`}>
                        <div className="trend-7d-hover-row-main">
                          <span className="trend-7d-hover-time">
                            {r.dayLabel ? `${r.dayLabel} · ` : ''}
                            {r.time || `Reading #${idx + 1}`}
                          </span>
                          {r.sys != null && r.dia != null ? (
                            <span style={{ fontWeight: 700, fontSize: '11px' }}>
                              <span style={{ color: getStatusColor(r.sysStatus) }}>{r.sys}</span>
                              <span style={{ color: '#64748b', margin: '0 1px' }}>/</span>
                              <span style={{ color: getStatusColor(r.diaStatus) }}>{r.dia}</span>
                              <span style={{ color: '#64748b', marginLeft: '3px', fontWeight: 500, fontSize: '9.5px' }}>mmHg</span>
                            </span>
                          ) : (
                            <span style={{ fontWeight: 700, fontSize: '11px', color: getStatusColor(r.status) }}>
                              {r.valDisplay}
                            </span>
                          )}
                        </div>
                        {r.isAbnormal ? (
                          <div className={`trend-7d-hover-review${reviewerLine ? ' is-done' : ' is-pending'}`}>
                            {reviewerLine || 'Not reviewed'}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default Trend7dCell;
